// Price resolution: which price applies to an item for a payer, scheme and facility at an
// instant (docs/plans/phase1-finance-catalogue.md §4, §5.1).
//
// The rule that matters most here is that no price is ever guessed. A missing assignment,
// a missing price, an inactive item or list, or an item not offered at the facility each
// has its own failure code. None of them falls back to zero or to the cash price: an
// insurer billed the cash price rejects the claim, and a charge of zero is revenue lost
// without anyone deciding to lose it. The billing slice turns each failure into words.
//
// The caller loads the candidate rows (assignments for the payer, the lists they point at
// and their bases, and the item's prices in those lists); this function only decides.

import type { EffectivePeriod } from './effective-period.ts';
import { periodContains } from './effective-period.ts';
import type { Money } from './money.ts';
import type { Result } from './result.ts';
import { err, ok } from './result.ts';

/** How far a list may fall back through base lists. Deep chains are a configuration smell. */
export const MAX_PRICE_LIST_DEPTH = 4;

export type ActiveStatus = 'active' | 'inactive';

export interface PricedItem {
  readonly id: string;
  readonly status: ActiveStatus;
  /** False when the item is marked "Not offered here" at the facility being priced. */
  readonly offeredAtFacility: boolean;
}

export interface PriceListRef {
  readonly id: string;
  readonly status: ActiveStatus;
  /** The single list this list falls back to for items it does not price. */
  readonly basePriceListId: string | null;
}

export interface PriceListAssignment {
  readonly id: string;
  readonly priceListId: string;
  readonly payerId: string;
  /** Null: every scheme of the payer. */
  readonly schemeId: string | null;
  /** Null: every facility. */
  readonly facilityId: string | null;
  readonly status: ActiveStatus;
}

export interface PriceRow {
  readonly id: string;
  readonly priceListId: string;
  readonly serviceItemId: string;
  readonly unitPrice: Money;
  readonly period: EffectivePeriod;
  /** Entered-in-error rows are ignored as if absent. */
  readonly status: 'active' | 'entered_in_error';
}

export interface PriceQuery {
  readonly item: PricedItem;
  readonly payerId: string;
  readonly schemeId: string | null;
  readonly facilityId: string;
  /** The clinical event time of the charge source (plan open question 2). */
  readonly at: Date;
  readonly assignments: readonly PriceListAssignment[];
  readonly priceLists: readonly PriceListRef[];
  readonly prices: readonly PriceRow[];
}

export interface ResolvedPrice {
  readonly priceId: string;
  /** The list the price row belongs to. */
  readonly priceListId: string;
  /** The list the assignment chose. Differs from `priceListId` when a base list was used. */
  readonly assignedPriceListId: string;
  readonly assignmentId: string;
  /** Lists passed through before the price was found, in order, starting with the assigned list. */
  readonly fellBackThrough: readonly string[];
  readonly unitPrice: Money;
}

export type PriceErrorCode =
  | 'price.item_inactive'
  | 'price.item_not_offered'
  | 'price.no_assignment'
  | 'price.overlapping_assignments'
  | 'price.price_list_inactive'
  | 'price.not_priced'
  | 'price.overlapping_prices'
  | 'price.price_list_cycle'
  | 'price.price_list_too_deep';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const PRICE_ERRORS: Record<PriceErrorCode, string> = {
  'price.item_inactive': 'This service or item is no longer in use',
  'price.item_not_offered': 'This service or item is not offered at this facility',
  'price.no_assignment': 'No price list is set up for this payer',
  'price.overlapping_assignments':
    'Two price lists are set up for this payer in the same place. An administrator must correct one',
  'price.price_list_inactive': "The payer's price list is no longer in use",
  'price.not_priced': 'This service or item has no price for this payer',
  'price.overlapping_prices':
    'This service or item has two prices for the same time. An administrator must correct one',
  'price.price_list_cycle': 'Price lists fall back to each other in a loop',
  'price.price_list_too_deep': `Price lists fall back through more than ${String(MAX_PRICE_LIST_DEPTH)} lists`,
};

/**
 * Specificity of an assignment for this query, or null when it does not apply.
 * 4: scheme + facility, 3: scheme, 2: payer + facility, 1: payer.
 */
function assignmentRank(a: PriceListAssignment, q: PriceQuery): number | null {
  if (a.status !== 'active' || a.payerId !== q.payerId) return null;
  if (a.facilityId !== null && a.facilityId !== q.facilityId) return null;
  if (a.schemeId !== null && a.schemeId !== q.schemeId) return null;
  const facilityPoint = a.facilityId === null ? 0 : 1;
  return a.schemeId === null ? 1 + facilityPoint : 3 + facilityPoint;
}

/**
 * The single most specific active assignment for the query. Two active assignments at the
 * same, highest specificity are reported rather than settled by picking one: the database's
 * unique constraint should make that impossible, and if it happens anyway the charge must
 * stop rather than be priced by chance.
 */
export function selectPriceListAssignment(
  q: PriceQuery,
): Result<PriceListAssignment, PriceErrorCode> {
  let best: PriceListAssignment[] = [];
  let bestRank = 0;
  for (const assignment of q.assignments) {
    const rank = assignmentRank(assignment, q);
    if (rank === null) continue;
    if (rank > bestRank) {
      best = [assignment];
      bestRank = rank;
    } else if (rank === bestRank) {
      best.push(assignment);
    }
  }
  if (best.length > 1) return err('price.overlapping_assignments');
  const assignment = best[0];
  return assignment ? ok(assignment) : err('price.no_assignment');
}

export function resolvePrice(q: PriceQuery): Result<ResolvedPrice, PriceErrorCode> {
  if (q.item.status !== 'active') return err('price.item_inactive');
  if (!q.item.offeredAtFacility) return err('price.item_not_offered');

  // Inactive assignments do not apply; an active assignment to an inactive list does,
  // and is reported, because silently using a less specific list would hide the mistake.
  const selected = selectPriceListAssignment(q);
  if (!selected.ok) return selected;
  const assignment = selected.value;

  const listsById = new Map(q.priceLists.map((list) => [list.id, list]));
  const visited: string[] = [];
  let listId: string | null = assignment.priceListId;

  while (listId !== null) {
    if (visited.includes(listId)) return err('price.price_list_cycle');
    if (visited.length >= MAX_PRICE_LIST_DEPTH) return err('price.price_list_too_deep');
    visited.push(listId);

    const list = listsById.get(listId);
    if (!list) throw new RangeError('Price list referenced but not loaded by the caller');
    if (list.status !== 'active') return err('price.price_list_inactive');

    const effective = q.prices.filter(
      (row) =>
        row.status === 'active' &&
        row.priceListId === listId &&
        row.serviceItemId === q.item.id &&
        periodContains(row.period, q.at),
    );
    if (effective.length > 1) return err('price.overlapping_prices');
    const row = effective[0];
    if (row) {
      return ok({
        priceId: row.id,
        priceListId: row.priceListId,
        assignedPriceListId: assignment.priceListId,
        assignmentId: assignment.id,
        fellBackThrough: visited.slice(0, -1),
        unitPrice: row.unitPrice,
      });
    }
    listId = list.basePriceListId;
  }
  return err('price.not_priced');
}

/**
 * Would setting `proposedBaseId` as the base of `listId` create a loop, or a chain deeper
 * than `MAX_PRICE_LIST_DEPTH`? Used by the API before saving a list.
 */
export function checkPriceListBase(
  lists: readonly PriceListRef[],
  listId: string,
  proposedBaseId: string | null,
): Result<null, PriceErrorCode> {
  if (proposedBaseId === null) return ok(null);
  const baseOf = new Map(lists.map((list) => [list.id, list.basePriceListId]));
  baseOf.set(listId, proposedBaseId);
  const seen = new Set<string>();
  let current: string | null = listId;
  let depth = 0;
  while (current !== null) {
    if (seen.has(current)) return err('price.price_list_cycle');
    seen.add(current);
    depth += 1;
    current = baseOf.get(current) ?? null;
  }
  return depth > MAX_PRICE_LIST_DEPTH ? err('price.price_list_too_deep') : ok(null);
}
